import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Trash2, Plus, Upload, Eye, Save, Rocket, Undo2 } from "lucide-react";
import api from "@/api";
import { compressImage } from "@/utils/image";
import { refreshPages } from "@/settings";

export const PAGE_TABS = [
  { key: "home", label: "Home", path: "/" },
  { key: "about", label: "About", path: "/about" },
  { key: "work", label: "Work", path: "/work" },
  { key: "services", label: "Services", path: "/services" },
  { key: "workshops", label: "Workshops", path: "/workshops" },
  { key: "shop", label: "Shop", path: "/shop" },
  { key: "contact", label: "Contact", path: "/contact" },
  { key: "global", label: "Global Settings", path: "/" },
];

const SCHEMAS = {
  home: [
    { key: "_note1", type: "note", text: "Hero headline, intro statement and contact details live in the Site Text tab (shared across pages). The hero logo is edited under Global Settings." },
    { key: "hero_primary_label", label: "Hero button 1 — label" },
    { key: "hero_primary_to", label: "Hero button 1 — destination (e.g. /work)" },
    { key: "hero_secondary_label", label: "Hero button 2 — label" },
    { key: "hero_secondary_to", label: "Hero button 2 — destination" },
    { key: "marquee_items", label: "Marquee words (comma separated)" },
    { key: "heading_architecture", label: "Section heading — Selected Architecture" },
    { key: "heading_art", label: "Section heading — Selected Art" },
    { key: "heading_services", label: "Section heading — Services" },
    { key: "heading_shop", label: "Section heading — Shop" },
    { key: "cta_line_1", label: "Closing message — line 1" },
    { key: "cta_line_2", label: "Closing message — line 2" },
    { key: "cta_primary_label", label: "Closing button 1 — label" },
    { key: "cta_secondary_label", label: "Closing button 2 — label" },
    { key: "cta_tertiary_label", label: "Closing button 3 — label" },
    { key: "featured_projects", label: "Featured projects (order matters)", type: "records", collection: "projects" },
    { key: "featured_artworks", label: "Featured artworks", type: "records", collection: "artworks" },
    { key: "featured_products", label: "Featured products", type: "records", collection: "products" },
    { key: "featured_workshop", label: "Featured workshop", type: "record-single", collection: "workshops" },
    { key: "show_architecture", label: "Show Selected Architecture section", type: "checkbox" },
    { key: "show_art", label: "Show Selected Art section", type: "checkbox" },
    { key: "show_about", label: "Show About statement section", type: "checkbox" },
    { key: "show_services", label: "Show Services section", type: "checkbox" },
    { key: "show_workshop", label: "Show Upcoming Workshop section", type: "checkbox" },
    { key: "show_shop", label: "Show Shop preview section", type: "checkbox" },
    { key: "show_cta", label: "Show closing enquiry section", type: "checkbox" },
  ],
  about: [
    { key: "heading_1", label: "Page heading — line 1" },
    { key: "heading_2", label: "Page heading — line 2" },
    { key: "who_heading", label: "Who We Are — heading" },
    { key: "who_text_2", label: "Who We Are — second paragraph", type: "textarea" },
    { key: "who_image", label: "Who We Are — large photograph", type: "image" },
    { key: "who_image_caption", label: "Photograph caption" },
    { key: "who_image_alt", label: "Photograph alt text (accessibility)" },
    { key: "vision_heading", label: "Our Vision — heading" },
    { key: "vision_intro", label: "Our Vision — intro text", type: "textarea" },
    { key: "vision_points", label: "Vision commitments", type: "points" },
    { key: "team_heading", label: "Our Team — heading" },
    { key: "show_vision", label: "Show Our Vision section", type: "checkbox" },
    { key: "show_founder", label: "Show Our Founder section", type: "checkbox" },
    { key: "_note1", type: "note", text: "Founder name, portrait and biography are edited in the Team tab. The Who We Are intro paragraph is in Site Text." },
  ],
  work: [
    { key: "heading_1", label: "Page heading — line 1" },
    { key: "heading_2", label: "Page heading — line 2" },
    { key: "arch_title", label: "Architecture panel — title" },
    { key: "arch_sub", label: "Architecture panel — subtitle" },
    { key: "arch_image", label: "Architecture panel — image", type: "image" },
    { key: "art_title", label: "Art panel — title" },
    { key: "art_sub", label: "Art panel — subtitle" },
    { key: "art_image", label: "Art panel — image", type: "image" },
  ],
  services: [
    { key: "heading_1", label: "Page heading — line 1" },
    { key: "heading_2", label: "Page heading — line 2" },
    { key: "form_heading", label: "Enquiry form — heading" },
    { key: "services", label: "Services", type: "services" },
    { key: "_note1", type: "note", text: "The enquiry form itself is a fixed functional section and cannot be removed." },
  ],
  workshops: [
    { key: "heading_1", label: "Page heading" },
    { key: "upcoming_label", label: "Upcoming section — label" },
    { key: "past_label", label: "Past workshops section — label" },
    { key: "_note1", type: "note", text: "Individual workshops (dates, fees, images, FAQs) are managed in the Workshops tab." },
  ],
  shop: [
    { key: "heading_1", label: "Page heading" },
    { key: "intro", label: "Introduction text", type: "textarea" },
    { key: "_note1", type: "note", text: "Individual products, prices and stock are managed in the Products tab. Checkout is a fixed functional section." },
  ],
  contact: [
    { key: "heading_1", label: "Page heading" },
    { key: "intro", label: "Introduction & response-time message", type: "textarea" },
    { key: "map_link", label: "Google Maps link (optional, https://…)" },
    { key: "_note1", type: "note", text: "Email, phone/WhatsApp, Instagram and studio location are edited in the Site Text tab so they stay consistent everywhere. The contact form is a fixed functional section." },
  ],
  global: [
    { key: "logo_url", label: "Header logo (light backgrounds)", type: "image" },
    { key: "logo_white_url", label: "Logo for dark backgrounds (footer, menu, admin)", type: "image" },
    { key: "nav_about", label: "Navigation label — About" },
    { key: "nav_work", label: "Navigation label — Work" },
    { key: "nav_services", label: "Navigation label — Services" },
    { key: "nav_workshops", label: "Navigation label — Workshops" },
    { key: "nav_shop", label: "Navigation label — Shop" },
    { key: "nav_contact", label: "Navigation label — Contact" },
    { key: "nav_community", label: "Navigation label — Community" },
    { key: "footer_note", label: "Footer — small note" },
    { key: "footer_copyright", label: "Footer — copyright line" },
    { key: "_note1", type: "note", text: "Payment credentials are never stored here — Razorpay links stay on individual products and workshops." },
  ],
};

const inputCls = "w-full bg-paper border border-ink/20 focus:border-brand outline-none px-3 py-2 text-sm";

function ImageField({ value, onChange, testid }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const rawFile = e.target.files?.[0];
    e.target.value = "";
    if (!rawFile) return;
    setUploading(true);
    try {
      const file = await compressImage(rawFile);
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-2">
      {value && <img src={value} alt="" className="h-20 w-auto border border-ink/15 object-cover" />}
      <div className="flex gap-2">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`${testid}-url`} />
        <button type="button" onClick={() => ref.current.click()} disabled={uploading} data-testid={`${testid}-upload`}
          className="shrink-0 inline-flex items-center gap-2 border border-ink/25 px-4 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-brand hover:text-brand transition-colors disabled:opacity-50">
          {uploading ? "Uploading…" : <><Upload size={12} /> Upload</>}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} />
    </div>
  );
}

function RecordsField({ collection, value, onChange, testid }) {
  const [items, setItems] = useState([]);
  const [pick, setPick] = useState("");
  useEffect(() => {
    api.get(`/${collection}`).then((r) => setItems(r.data)).catch(() => {});
  }, [collection]);
  const ids = value || [];
  const selected = ids.map((id) => items.find((x) => x.id === id)).filter(Boolean);
  const available = items.filter((x) => !ids.includes(x.id));
  const move = (i, dir) => {
    const a = [...ids];
    const [x] = a.splice(i, 1);
    a.splice(Math.max(0, Math.min(a.length, i + dir)), 0, x);
    onChange(a);
  };
  const nameOf = (x) => x.title || x.name;
  return (
    <div data-testid={testid} className="space-y-2">
      {selected.map((it, i) => (
        <div key={it.id} className="flex items-center gap-3 border border-ink/15 bg-linen px-3 py-2">
          <span className="font-mono text-[10px] text-ink/40">{i + 1}.</span>
          <span className="flex-1 text-sm truncate">{nameOf(it)}</span>
          <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="p-1 hover:text-brand disabled:opacity-30"><ChevronUp size={14} /></button>
          <button type="button" onClick={() => move(i, 1)} disabled={i === selected.length - 1} aria-label="Move down" className="p-1 hover:text-brand disabled:opacity-30"><ChevronDown size={14} /></button>
          <button type="button" onClick={() => onChange(ids.filter((x) => x !== it.id))} aria-label="Remove" className="p-1 hover:text-brand"><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)} className={inputCls} data-testid={`${testid}-select`}>
          <option value="">Choose from existing {collection}…</option>
          {available.map((x) => <option key={x.id} value={x.id}>{nameOf(x)}</option>)}
        </select>
        <button type="button" disabled={!pick}
          onClick={() => { onChange([...ids, pick]); setPick(""); }}
          data-testid={`${testid}-add`}
          className="shrink-0 inline-flex items-center gap-2 border border-ink/25 px-4 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-brand hover:text-brand transition-colors disabled:opacity-50">
          <Plus size={12} /> Add
        </button>
      </div>
      {!ids.length && <p className="font-mono text-[10px] text-ink/40">Empty — the page falls back to items marked "Featured" in their own editors.</p>}
    </div>
  );
}

function SingleRecordField({ collection, value, onChange, testid }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/${collection}`).then((r) => setItems(r.data)).catch(() => {});
  }, [collection]);
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
      <option value="">Automatic — latest upcoming workshop</option>
      {items.filter((x) => x.status === "upcoming").map((x) => <option key={x.id} value={x.id}>{x.title || x.name}</option>)}
    </select>
  );
}

function ServicesField({ value, onChange, testid }) {
  const items = value || [];
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const move = (i, dir) => {
    const a = [...items];
    const [x] = a.splice(i, 1);
    a.splice(Math.max(0, Math.min(a.length, i + dir)), 0, x);
    onChange(a);
  };
  const remove = (i) => {
    if (!window.confirm(`Remove "${items[i].name}"? You can also hide it instead to keep it for later.`)) return;
    onChange(items.filter((_, idx) => idx !== i));
  };
  return (
    <div data-testid={testid} className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className={`border border-ink/15 bg-linen p-3 ${it.hidden ? "opacity-50" : ""}`} data-testid={`${testid}-item-${i}`}>
          <div className="flex items-center gap-2">
            <input value={it.name} onChange={(e) => update(i, "name", e.target.value)} className={inputCls} data-testid={`${testid}-name-${i}`} />
            <select value={it.group} onChange={(e) => update(i, "group", e.target.value)} className={`${inputCls} w-36 shrink-0`}>
              <option>Architecture</option>
              <option>Art</option>
            </select>
          </div>
          <input value={it.description || ""} onChange={(e) => update(i, "description", e.target.value)} placeholder="Short description (optional)" className={`${inputCls} mt-2`} data-testid={`${testid}-desc-${i}`} />
          <div className="flex items-center gap-3 mt-2">
            <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/50 cursor-pointer">
              <input type="checkbox" checked={!!it.hidden} onChange={(e) => update(i, "hidden", e.target.checked)} className="w-3.5 h-3.5 accent-[#E53827]" data-testid={`${testid}-hidden-${i}`} />
              Hidden
            </label>
            <span className="flex-1" />
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="p-1 hover:text-brand disabled:opacity-30"><ChevronUp size={14} /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down" className="p-1 hover:text-brand disabled:opacity-30"><ChevronDown size={14} /></button>
            <button type="button" onClick={() => remove(i)} aria-label="Remove service" className="p-1 hover:text-brand" data-testid={`${testid}-remove-${i}`}><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { name: "New service", group: "Architecture", description: "", hidden: false }])}
        data-testid={`${testid}-add`}
        className="inline-flex items-center gap-2 border border-ink/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-brand hover:text-brand transition-colors">
        <Plus size={12} /> Add service
      </button>
    </div>
  );
}

function PointsField({ value, onChange, testid }) {
  const items = value || [];
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const move = (i, dir) => {
    const a = [...items];
    const [x] = a.splice(i, 1);
    a.splice(Math.max(0, Math.min(a.length, i + dir)), 0, x);
    onChange(a);
  };
  return (
    <div data-testid={testid} className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border border-ink/15 bg-linen p-3">
          <div className="flex items-center gap-2">
            <input value={it.title} onChange={(e) => update(i, "title", e.target.value)} className={inputCls} data-testid={`${testid}-title-${i}`} />
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="p-1 hover:text-brand disabled:opacity-30"><ChevronUp size={14} /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down" className="p-1 hover:text-brand disabled:opacity-30"><ChevronDown size={14} /></button>
            <button type="button" onClick={() => { if (window.confirm("Remove this point?")) onChange(items.filter((_, idx) => idx !== i)); }} aria-label="Remove" className="p-1 hover:text-brand"><Trash2 size={14} /></button>
          </div>
          <textarea rows={2} value={it.text} onChange={(e) => update(i, "text", e.target.value)} className={`${inputCls} mt-2 resize-y`} data-testid={`${testid}-text-${i}`} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { title: "New point", text: "" }])}
        data-testid={`${testid}-add`}
        className="inline-flex items-center gap-2 border border-ink/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-brand hover:text-brand transition-colors">
        <Plus size={12} /> Add point
      </button>
    </div>
  );
}

function PageEditor({ slug, path }) {
  const [form, setForm] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");

  const load = () => {
    setForm(null);
    api.get(`/admin/pages/${slug}`).then((r) => {
      setForm(r.data.content);
      setHasDraft(r.data.has_draft);
      setDirty(false);
    }).catch(() => toast.error("Could not load page content."));
  };

  useEffect(() => { load(); }, [slug]);

  useEffect(() => {
    const h = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  if (!form) return <p className="font-mono text-sm text-ink/40">Loading…</p>;

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const saveDraft = async () => {
    setBusy("draft");
    try {
      await api.put(`/admin/pages/${slug}`, form);
      setHasDraft(true);
      setDirty(false);
      toast.success("Draft saved — not live yet.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      await api.put(`/admin/pages/${slug}`, form);
      await api.post(`/admin/pages/${slug}/publish`);
      refreshPages();
      setHasDraft(true);
      setDirty(false);
      toast.success("Published — live on the website.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed.");
    } finally {
      setBusy("");
    }
  };

  const discard = async () => {
    if (!window.confirm("Discard the unpublished draft and revert to the live version?")) return;
    setBusy("discard");
    try {
      await api.post(`/admin/pages/${slug}/discard`);
      toast.success("Draft discarded.");
      load();
    } catch {
      toast.error("Discard failed.");
    } finally {
      setBusy("");
    }
  };

  const preview = () => {
    if (dirty) {
      toast.error("Save the draft first, then preview.");
      return;
    }
    window.open(`${path}?preview_draft=${slug}`, "_blank");
  };

  const status = dirty
    ? "Unsaved changes"
    : hasDraft
      ? "Editing draft — publish to go live"
      : "Live version";

  return (
    <div className="max-w-2xl" data-testid={`page-editor-${slug}`}>
      <div className="flex flex-wrap items-center gap-2 mb-8 sticky top-0 bg-linen py-3 z-10 border-b border-ink/10">
        <span className={`font-mono text-[10px] uppercase tracking-[0.2em] mr-2 ${dirty ? "text-brand" : "text-ink/50"}`} data-testid="page-editor-status">
          {status}
        </span>
        <span className="flex-1" />
        <button onClick={preview} data-testid="page-preview-btn"
          className="inline-flex items-center gap-2 border border-ink/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-brand hover:text-brand transition-colors">
          <Eye size={12} /> Preview
        </button>
        <button onClick={saveDraft} disabled={!!busy} data-testid="page-save-draft-btn"
          className="inline-flex items-center gap-2 border border-ink px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-brand hover:text-brand transition-colors disabled:opacity-50">
          <Save size={12} /> {busy === "draft" ? "Saving…" : "Save Draft"}
        </button>
        <button onClick={publish} disabled={!!busy} data-testid="page-publish-btn"
          className="inline-flex items-center gap-2 bg-ink text-paper px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-brand transition-colors disabled:opacity-50">
          <Rocket size={12} /> {busy === "publish" ? "Publishing…" : "Publish Changes"}
        </button>
        {hasDraft && (
          <button onClick={discard} disabled={!!busy} data-testid="page-discard-btn"
            className="inline-flex items-center gap-2 border border-brand/50 text-brand px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-brand hover:text-paper transition-colors disabled:opacity-50">
            <Undo2 size={12} /> Discard Draft
          </button>
        )}
      </div>

      <div className="space-y-5">
        {SCHEMAS[slug].map((f) => (
          <div key={f.key}>
            {f.type === "note" ? (
              <p className="text-xs text-ink/50 bg-linen border border-ink/10 px-4 py-3 leading-relaxed">{f.text}</p>
            ) : (
              <>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 mb-1.5">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea rows={3} value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className={`${inputCls} resize-y`} data-testid={`page-field-${f.key}`} />
                ) : f.type === "checkbox" ? (
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!form[f.key]} onChange={(e) => set(f.key, e.target.checked)} className="w-4 h-4 accent-[#E53827]" data-testid={`page-field-${f.key}`} />
                    <span className="text-sm text-ink/60">{form[f.key] ? "Visible" : "Hidden"}</span>
                  </label>
                ) : f.type === "image" ? (
                  <ImageField value={form[f.key]} onChange={(v) => set(f.key, v)} testid={`page-field-${f.key}`} />
                ) : f.type === "records" ? (
                  <RecordsField collection={f.collection} value={form[f.key]} onChange={(v) => set(f.key, v)} testid={`page-field-${f.key}`} />
                ) : f.type === "record-single" ? (
                  <SingleRecordField collection={f.collection} value={form[f.key]} onChange={(v) => set(f.key, v)} testid={`page-field-${f.key}`} />
                ) : f.type === "services" ? (
                  <ServicesField value={form[f.key]} onChange={(v) => set(f.key, v)} testid={`page-field-${f.key}`} />
                ) : f.type === "points" ? (
                  <PointsField value={form[f.key]} onChange={(v) => set(f.key, v)} testid={`page-field-${f.key}`} />
                ) : (
                  <input value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className={inputCls} data-testid={`page-field-${f.key}`} />
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function PagesTab() {
  const [page, setPage] = useState("home");
  const current = PAGE_TABS.find((p) => p.key === page);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-8" data-testid="pages-subtabs">
        {PAGE_TABS.map((p) => (
          <button key={p.key} onClick={() => setPage(p.key)} data-testid={`page-tab-${p.key}`}
            className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
              page === p.key ? "bg-ink text-paper border-ink" : "border-ink/20 text-ink/60 hover:border-brand hover:text-brand"
            }`}>
            {p.label}
          </button>
        ))}
      </div>
      <PageEditor key={page} slug={page} path={current.path} />
    </div>
  );
}
